import {
  Node,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from '@tiptap/react';
import { LinkUnfurlCard } from './link-unfurl-card';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkUnfurl: {
      /** Insert a link-unfurl card for `href` at the current selection. */
      insertLinkUnfurl: (href: string) => ReturnType;
    };
  }
}

/** React node view — renders the rich preview card for the stored href. */
function LinkUnfurlNodeView({ node, getPos, editor }: ReactNodeViewProps) {
  const href = (node.attrs.href as string) ?? '';
  return (
    <NodeViewWrapper data-link-unfurl-view>
      <LinkUnfurlCard
        href={href}
        onRemove={() => {
          // Replace the card with a plain link to the same URL.
          if (typeof getPos !== 'function') return;
          const pos = getPos();
          if (pos == null) return;
          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: href,
                  marks: [{ type: 'link', attrs: { href } }],
                },
              ],
            })
            .run();
        }}
      />
    </NodeViewWrapper>
  );
}

/**
 * Link-unfurl card — a block-level atom node carrying just an external URL
 * (`href`). Renders as a rich preview card ({@link LinkUnfurlCard}) that
 * resolves OpenGraph metadata via the nexus `/api/og` endpoint. The Yjs doc
 * commits only to "there's a card for this URL here"; the volatile metadata
 * is resolved + cached out-of-band (server-side), keeping the CRDT clean.
 *
 * Serializes to `<div data-link-unfurl data-href="…">` so it round-trips
 * through the Yjs doc / HTML clipboard. Inserted by the paste rule (a lone URL
 * pasted into an empty paragraph) or the `insertLinkUnfurl` command.
 */
export const LinkUnfurlExtension = Node.create({
  name: 'linkUnfurl',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      href: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-href') ?? '',
        renderHTML: (attrs) => ({ 'data-href': attrs.href as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-link-unfurl]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { ...HTMLAttributes, 'data-link-unfurl': '' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkUnfurlNodeView);
  },

  addCommands() {
    return {
      insertLinkUnfurl:
        (href: string) =>
        ({ commands }: { commands: import('@tiptap/core').SingleCommands }) => {
          const trimmed = href?.trim();
          if (!trimmed) return false;
          return commands.insertContent({ type: this.name, attrs: { href: trimmed } });
        },
    };
  },
});
